import { ChangeEvent, FC } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { InputWrapperLarge } from '../../../../common/FormElementWrappers/FormElementWrappers.styles';
import { H2 } from '../../../../common/Headings/Headings.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Input from '../../../../components/Input/Input';
import MaskedInput from '../../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../../components/RadioGroup/RadioGroup.types';
import { Buttons, FprodInputs, HorizontalLine, Inputs, UpdateRecipeDetailsPage } from './UpdateRecipeDetails.styles';
import { DescriptionUpdateRule, FprodUpdateRule, UpdateRecipeDetailsProps } from './UpdateRecipeDetails.types';

const UpdateRecipeDetails: FC<UpdateRecipeDetailsProps> = ({
  selectedRecipes,
  setStep,
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
}) => {
  const mapIdToEnumFprod = {
    fprodNoAction: FprodUpdateRule.NO_ACTION,
    fprodNew: FprodUpdateRule.APPLY_NEW,
    fprodCopy: FprodUpdateRule.COPY_EXISTING,
  };
  const mapIdToEnumDesc = {
    descNoAction: DescriptionUpdateRule.NO_ACTION,
    descReplace: DescriptionUpdateRule.REPLACE_DESCRIPTION,
    descAppend: DescriptionUpdateRule.APPEND_LONG_DESCRIPTION,
  };
  const mapEnumToIdFprod = {
    [FprodUpdateRule.NO_ACTION]: 'fprodNoAction',
    [FprodUpdateRule.APPLY_NEW]: 'fprodNew',
    [FprodUpdateRule.COPY_EXISTING]: 'fprodCopy',
  };
  const mapEnumToIdDesc = {
    [DescriptionUpdateRule.NO_ACTION]: 'descNoAction',
    [DescriptionUpdateRule.REPLACE_DESCRIPTION]: 'descReplace',
    [DescriptionUpdateRule.APPEND_LONG_DESCRIPTION]: 'descAppend',
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>, ...params: any[]) => {
    if (formErrors[event.target.name as keyof typeof formErrors]) {
      setFormErrors({ ...formErrors, [event.target.name]: '' });
    }
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleFprodChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (formErrors.fmsProdNo !== '') {
      setFormErrors({ ...formErrors, fmsProdNo: '' });
    }
    setFormValues({ ...formValues, fmsProdNo: values.value });
  };

  const handleFprodUpdateRuleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const id = event.target.id as keyof typeof mapIdToEnumFprod;
    const newUpdateRule: FprodUpdateRule = mapIdToEnumFprod[id];
    setFormValues({ ...formValues, fprodUpdateRule: newUpdateRule });
  };
  const handleDescriptionUpdateRuleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const id = event.target.id as keyof typeof mapIdToEnumDesc;
    const newUpdateRule: DescriptionUpdateRule = mapIdToEnumDesc[id];
    setFormValues({ ...formValues, descriptionUpdateRule: newUpdateRule });
  };

  const handleNextClick = () => {
    const { description, longDescription, fmsProdNo, fprodUpdateRule, descriptionUpdateRule } = formValues;
    let error = false;
    let errorMessages = {
      description: '',
      longDescription: '',
      fmsProdNo: '',
    };
    // new fprod
    if (fprodUpdateRule === FprodUpdateRule.APPLY_NEW) {
      if (fmsProdNo === '') {
        errorMessages.fmsProdNo = 'Please input a valid fmsProdNo';
        error = true;
      }
    }
    // replacing descriptions
    if (descriptionUpdateRule === DescriptionUpdateRule.REPLACE_DESCRIPTION) {
      if (description.length > 32) {
        errorMessages.description = 'Description must be 32 characters or less';
        error = true;
      } else if (description.length === 0) {
        errorMessages.description = 'Please input a valid description';
        error = true;
      }
      if (longDescription.length > 255) {
        errorMessages.longDescription = 'Long Description must be 255 characters or less';
        error = true;
      } else if (longDescription.length === 0) {
        errorMessages.longDescription = 'Please input a valid long description';
        error = true;
      }
    }
    // appending long description
    if (descriptionUpdateRule === DescriptionUpdateRule.APPEND_LONG_DESCRIPTION) {
      // find the longest long description selected and validate that it isn't 255 when combined?
      const selectedRecipeDescriptions = selectedRecipes
        .map((recipe) => recipe.longDescription)
        .sort((a, b) => b.length - a.length);
      const newLongestDescription = selectedRecipeDescriptions[0] + longDescription;
      if (newLongestDescription.length > 255) {
        errorMessages.longDescription = `Long Descriptions must be 255 characters or less- remove ${
          newLongestDescription.length - 255
        } characters or update the recipes selected`;
        error = true;
      }
      if (longDescription === '') {
        errorMessages.longDescription = 'Please include a value to append';
        error = true;
      }
    }
    if (!error) {
      setFormErrors({ description: '', longDescription: '', fmsProdNo: '' });
      setStep(2);
    } else {
      setFormErrors(errorMessages);
    }
  };

  return (
    <UpdateRecipeDetailsPage>
      <H2>Description Update</H2>
      <Inputs>
        <InputWrapperLarge>
          <Input
            label='Description'
            id='description'
            name='description'
            value={formValues.description}
            onChange={handleInputChange}
            disabled={formValues.descriptionUpdateRule !== DescriptionUpdateRule.REPLACE_DESCRIPTION}
            errorText={formErrors.description}
          />
        </InputWrapperLarge>
        <InputWrapperLarge>
          <Input
            label='Long Description'
            id='longDescription'
            name='longDescription'
            value={formValues.longDescription}
            onChange={handleInputChange}
            disabled={formValues.descriptionUpdateRule === DescriptionUpdateRule.NO_ACTION}
            errorText={formErrors.longDescription}
          />
        </InputWrapperLarge>
      </Inputs>
      <RadioGroup
        id='descriptionStrategy'
        layout={RadioGroupLayouts.Row}
        label=''
        aria-label='choose description update strategy'
        selected={mapEnumToIdDesc[formValues.descriptionUpdateRule]}
        options={[
          { id: 'descAppend', label: 'Append to existing long description' },
          { id: 'descReplace', label: 'Replace descriptions with new descriptions' },
          { id: 'descNoAction', label: 'Do not update descriptions' },
        ]}
        onChange={handleDescriptionUpdateRuleChange}
      />
      <HorizontalLine />
      <H2>FPROD Update</H2>
      <FprodInputs>
        <RadioGroup
          id='fprodStrategy'
          layout={RadioGroupLayouts.Row}
          label=''
          aria-label='choose fprod update strategy'
          selected={mapEnumToIdFprod[formValues.fprodUpdateRule]}
          options={[
            { id: 'fprodCopy', label: 'Copy existing FPROD' },
            { id: 'fprodNew', label: 'Apply new FPROD' },
            { id: 'fprodNoAction', label: 'Do not copy/apply FPROD' },
          ]}
          onChange={handleFprodUpdateRuleChange}
        />
        <InputWrapperLarge>
          <MaskedInput
            label='FPROD'
            id='fmsProdNo'
            name='fmsProdNo'
            value={formValues.fmsProdNo}
            onValueChange={handleFprodChange}
            disabled={formValues.fprodUpdateRule !== FprodUpdateRule.APPLY_NEW}
            errorText={formErrors.fmsProdNo}
            format='#####'
          />
        </InputWrapperLarge>
      </FprodInputs>
      <Buttons>
        <Button
          label='Back'
          variant={Variant.Secondary}
          onClick={() => {
            setFormErrors({ description: '', longDescription: '', fmsProdNo: '' });
            setStep(0);
          }}
        />
        <Button label='Next' onClick={handleNextClick} />
      </Buttons>
    </UpdateRecipeDetailsPage>
  );
};

export default UpdateRecipeDetails;
